/*
 * Swym Wishlist Plus stays installed for its SDK and backend, but none of its injected UI is used: its own buttons
 * always resolve a product's default variant, so for a shopper who saved any other variant the state it shows and the
 * item it removes disagree. Everything here targets the exact variant instead.
 */
;(() => {
  if (window.customElements.get('wishlist-button')) return

  const same = (a, b) => String(a) === String(b)

  const store = { swat: null, listId: null, items: [], subscribers: new Set() }

  const isVariantSaved = (empi, epi) => store.items.some((i) => same(i.empi, empi) && same(i.epi, epi))
  const isProductSaved = (empi) => store.items.some((i) => same(i.empi, empi))
  const savedVariantsOf = (empi) => store.items.filter((i) => same(i.empi, empi)).map((i) => i.epi)

  const publish = () => {
    store.subscribers.forEach((notify) => notify())
    document.dispatchEvent(new CustomEvent('wishlist:changed', { detail: { items: store.items } }))
  }

  const load = () =>
    new Promise((resolve) => {
      store.swat.fetch(
        (items) => {
          store.items = items || []
          store.listId = store.items.find((i) => i.lid)?.lid || null
          resolve()
        },
        () => resolve()
      )
    })

  const ready = new Promise((resolve) => {
    window.SwymCallbacks = window.SwymCallbacks || []
    window.SwymCallbacks.push((swat) => {
      store.swat = swat
      load().then(resolve)
    })
  })

  // Swym's feature switches live in its runtime config, not in theme settings.
  const applyFeatureFlags = () => {
    const settings = store.swat?.retailerSettings || {}
    const features = window.SwymEnabledCommonFeatures || {}
    const wishlist = settings.Wishlist || {}

    const flags = {
      'wishlist-off': wishlist.Enabled === false || wishlist.EnableSwymWishlist === false,
      'wishlist-sharing-off': wishlist.AllowSharing !== true,
      'wishlist-email-share-off': settings.Email?.EnableEmailFriend !== true,
      'wishlist-login-nudge': features['login-nudge'] === true,
    }

    for (const [name, on] of Object.entries(flags)) {
      document.documentElement.classList.toggle(name, on)
    }
  }

  ready.then(applyFeatureFlags)

  const refresh = () => {
    if (!store.swat) return
    load().then(publish)
  }

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refresh()
  })

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') refresh()
  })

  // Swym merges a guest list into the account list shortly after sign in, with no event to hang this off.
  ready.then(() => {
    if (!store.swat?.platform?.isLoggedIn?.()) return

    setTimeout(refresh, 1500)
    setTimeout(refresh, 5000)
  })

  const listId = async () => {
    if (!store.listId) await load()
    return store.listId
  }

  const add = (product) =>
    new Promise((resolve) => {
      store.swat.addToWishList(
        { qty: 1, ...product },
        () => {
          store.items = [...store.items, { lid: store.listId, ...product }]
          publish()
          resolve(true)
        },
        () => resolve(false)
      )
    })

  const remove = async ({ empi, epi, du }) => {
    const lid = await listId()
    if (!lid) return false

    const done = await new Promise((resolve) => {
      store.swat.deleteFromList(lid, { epi, empi, du }, () => resolve(true), () => resolve(false))
    })

    if (done) {
      store.items = store.items.filter((i) => !(same(i.empi, empi) && same(i.epi, epi)))
      publish()
    }
    return done
  }

  const announce = (state) => {
    const host = document.querySelector('wishlist-notification')
    const template = host?.querySelector(`template[data-${state}]`)
    // Rendered into its own slot so the templates survive for the next notification.
    const slot = host?.querySelector('[data-wishlist-banner]')
    if (!template || !slot) return

    slot.replaceChildren(template.content.cloneNode(true))
    host.setAttribute('open', '')

    clearTimeout(host.timer)
    host.timer = setTimeout(() => {
      host.removeAttribute('open')
      slot.replaceChildren()
    }, 4000)
  }

  class WishlistButton extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button')
      this.button?.addEventListener('click', () => this.toggle())

      if (this.dataset.context === 'product') {
        this.closest('form')?.addEventListener('variant:change', (event) =>
          this.setVariant(event.detail.variant)
        )
      }

      this.subscription = () => this.reflect()
      store.subscribers.add(this.subscription)
      ready.then(() => this.reflect())
    }

    disconnectedCallback() {
      store.subscribers.delete(this.subscription)
    }

    setVariant(variant) {
      if (!variant) return

      this.dataset.epi = variant.id
      this.dataset.pr = variant.price / 100
      this.dataset.uri = `${this.dataset.du}?variant=${variant.id}`
      this.reflect()
    }

    get isSaved() {
      const { context, empi, epi } = this.dataset
      return context === 'card' ? isProductSaved(empi) : isVariantSaved(empi, epi)
    }

    reflect() {
      if (!this.button) return

      const saved = this.isSaved
      this.button.setAttribute('aria-pressed', String(saved))
      this.button.setAttribute('aria-label', saved ? this.dataset.labelRemove : this.dataset.labelAdd)
    }

    async toggle() {
      if (!store.swat) return

      this.button.setAttribute('aria-busy', 'true')
      const done = await (this.isSaved ? this.removeSaved() : this.addSaved())
      this.button.removeAttribute('aria-busy')

      if (!done) announce('error')
    }

    async addSaved() {
      if (this.dataset.context === 'card' && this.hasAttribute('data-pick-variant')) {
        document.querySelector('wishlist-drawer')?.pick(this)
        return true
      }

      const { empi, epi, du, dt, iu, pr, uri } = this.dataset
      const done = await add({ empi: Number(empi), epi: Number(epi), du, dt, iu, pr: Number(pr), uri })

      if (done) announce('added')
      return done
    }

    async removeSaved() {
      const { context, empi, epi, du } = this.dataset
      const variants = context === 'card' ? savedVariantsOf(empi) : [epi]
      let done = true

      for (const variant of variants) {
        done = (await remove({ empi: Number(empi), epi: Number(variant), du })) && done
      }

      if (done) announce('removed')
      return done
    }
  }

  class WishlistDrawer extends HTMLElement {
    connectedCallback() {
      // x-drawer moves itself to the body when opened, so its parts are resolved up front.
      this.dialog = this.querySelector('x-drawer')
      this.heading = this.querySelector('[data-wishlist-drawer-title]')
      this.list = this.querySelector('[data-wishlist-variants]')
      this.optionTemplate = this.querySelector('template[data-wishlist-variant]')
    }

    async pick(trigger) {
      const { list, optionTemplate: template } = this

      this.heading.textContent = trigger.dataset.dt
      list.replaceChildren()
      this.dialog.show()

      const product = await (
        await fetch(`${Shopify.routes.root}products/${trigger.dataset.handle}.js`)
      ).json()

      list.replaceChildren(
        ...product.variants.map((variant) => {
          const option = template.content.cloneNode(true).firstElementChild

          option.querySelector('span').textContent = variant.title
          option.disabled = !variant.available
          option.addEventListener('click', () => this.save(trigger, variant, product))

          return option
        })
      )
    }

    async save(trigger, variant, product) {
      const { empi, du, dt } = trigger.dataset
      const image = variant.featured_image?.src || product.featured_image || trigger.dataset.iu

      const done = await add({
        empi: Number(empi),
        epi: variant.id,
        du,
        dt,
        iu: image?.startsWith('//') ? `https:${image}` : image,
        pr: variant.price / 100,
        uri: `${du}?variant=${variant.id}`,
      })

      this.dialog.hide()
      announce(done ? 'added' : 'error')
    }
  }

  class WishlistCount extends HTMLElement {
    connectedCallback() {
      this.subscription = () => this.reflect()
      store.subscribers.add(this.subscription)
      ready.then(() => this.reflect())
    }

    disconnectedCallback() {
      store.subscribers.delete(this.subscription)
    }

    reflect() {
      this.querySelector('[data-wishlist-total]').textContent = store.items.length
      this.classList.toggle('opacity-0', store.items.length === 0)
    }
  }

  window.customElements.define('wishlist-button', WishlistButton)
  window.customElements.define('wishlist-drawer', WishlistDrawer)
  window.customElements.define('wishlist-count', WishlistCount)
})()
